import { FC, useContext, useId, useMemo } from "react";
import { MAP_CONTENT_MEMBER_GROUP, MAP_CONTENT_MEMBER_LAYER_ORDER, MAP_CONTENT_PROVIDER_OPTION_ORDER } from "@app/projects/network-assessment/multi-map-export/utils/mapContentOptions";
import { SwatchCheckboxGroup } from "@app/projects/network-assessment/multi-map-export/components/SwatchCheckboxGroup";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { UserContext } from "@app/user/UserContext";
import { MultiMapExportAction } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportMemberLayer } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportSection } from "@app/projects/network-assessment/multi-map-export/components/MultiMapExportSection";
import { canSeeTerminatedProviders, retainVisibleProviderOptions, visibleProviderGroups } from "@app/projects/network-assessment/multi-map-export/utils/mapContentSelection";

const SECTION_NUMBER = 5;
const TEST_ID_PREFIX = "multi-map-export__map-content";

export const MapContentSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { selections, selectionsChanged } = useContext(MultiMapExportContext);
  // UserContext's default value is null, so this is optional even inside the app.
  const user = useContext(UserContext);
  const membersLabelId = useId();
  const networkLabelId = useId();
  const outOfNetworkLabelId = useId();

  const selectedMemberLayers = selections?.selectedMapContentMemberLayers ?? [];
  const selectedProviderOptions = selections?.selectedMapContentProviderOptions ?? [];

  const groups = useMemo(() => visibleProviderGroups(canSeeTerminatedProviders(user?.roles)), [user?.roles]);
  const networkGroups = useMemo(() => groups.filter((group) => group.key === ProvidersFilterOptions.Network), [groups]);
  const outOfNetworkGroups = useMemo(() => groups.filter((group) => group.key !== ProvidersFilterOptions.Network), [groups]);

  const handleMemberLayersChange = (layers: MultiMapExportMemberLayer[]) => selectionsChanged?.({
    type: MultiMapExportAction.MapContentChanged,
    value: { selectedMapContentMemberLayers: layers },
  });

  // Filtered on the way in, so an option this user cannot see can never reach the export payload.
  const handleProviderOptionsChange = (options: ProvidersFilterOptions[]) => selectionsChanged?.({
    type: MultiMapExportAction.MapContentChanged,
    value: { selectedMapContentProviderOptions: retainVisibleProviderOptions(options, groups) },
  });

  return (
    <MultiMapExportSection
      sectionNumber={SECTION_NUMBER}
      subHeader="Select which data points to plot on the map. Applied to every map."
      testId="multi-map-export__map-content-section"
      title="Map Content"
    >
      <div className="multi-map-export__map-content-columns">
        <div aria-labelledby={membersLabelId} className="multi-map-export__map-content-column" role="group">
          <span className="multi-map-export__field-label" id={membersLabelId}>Members</span>
          <SwatchCheckboxGroup
            canonicalOrder={MAP_CONTENT_MEMBER_LAYER_ORDER}
            group={MAP_CONTENT_MEMBER_GROUP}
            onChange={handleMemberLayersChange}
            selectedValues={selectedMemberLayers}
            testIdPrefix={TEST_ID_PREFIX}
          />
        </div>
        <div aria-labelledby={networkLabelId} className="multi-map-export__map-content-column" role="group">
          <span className="multi-map-export__field-label" id={networkLabelId}>Network Providers</span>
          {networkGroups.map((group) => (
            <SwatchCheckboxGroup
              canonicalOrder={MAP_CONTENT_PROVIDER_OPTION_ORDER}
              group={group}
              key={group.key}
              onChange={handleProviderOptionsChange}
              selectedValues={selectedProviderOptions}
              testIdPrefix={TEST_ID_PREFIX}
            />
          ))}
        </div>
        <div aria-labelledby={outOfNetworkLabelId} className="multi-map-export__map-content-column" role="group">
          <span className="multi-map-export__field-label" id={outOfNetworkLabelId}>Out-of-Network Providers</span>
          {outOfNetworkGroups.map((group) => (
            <SwatchCheckboxGroup
              canonicalOrder={MAP_CONTENT_PROVIDER_OPTION_ORDER}
              group={group}
              key={group.key}
              onChange={handleProviderOptionsChange}
              selectedValues={selectedProviderOptions}
              testIdPrefix={TEST_ID_PREFIX}
            />
          ))}
        </div>
      </div>
    </MultiMapExportSection>
  );
};

export default MapContentSection;
